// components/demos/DemoEntryForm.jsx
import React, { useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useGetEmployees } from "../../hooks/useEmployeeApi";
import { Trash2 } from "lucide-react";
import { useAuth } from "../../hooks/AuthContext";

export const DemoEntryForm = ({ onSubmit, isLoading }) => {
  const { user } = useAuth();

  const {
    register,
    control,
    handleSubmit,
    watch, 
    setValue, 
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      demos: [
        {
          demoName: "",
          type: "",
          hostBy: "",
          datetime: "",
          createdBy: user?.employeeProfile?._id,
        },
      ],
    },
  });

  const { data: salesEmployees, isLoading: isLoadingSales } = useGetEmployees();
  const { fields, append, remove } = useFieldArray({ control, name: "demos" });


  const demosWatch = watch("demos");

  useEffect(() => {
    demosWatch.forEach((demo, index) => {
      if (demo.type && demo.datetime) {
        const dateObj = new Date(demo.datetime);

        // Format date and time from selected datetime
        const formattedDate = dateObj.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
        const formattedTime = dateObj.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

        const typeLabel = demo.type === "demo" ? "Demo" : "Training";
        const generatedName = `${typeLabel} - ${formattedDate} - ${formattedTime}`;

        if (demo.demoName !== generatedName) {
          setValue(`demos.${index}.demoName`, generatedName);
        }
      }
    });
  }, [demosWatch, setValue]);


  const handleFormSubmit = (data) => {
    const updatedDemos = data.demos.map((demo) => {
      const dateObj = demo.datetime ? new Date(demo.datetime) : new Date();
  
      const formattedDate = dateObj.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
      const formattedTime = dateObj.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
  
      return {
        ...demo,
        demoName:
          demo.demoName && demo.demoName.trim() !== ""
            ? demo.demoName
            : `${demo.type === "demo" ? "Demo" : "Training"} - ${formattedDate} - ${formattedTime}`,
        datetime: demo.datetime
          ? Math.floor(new Date(demo.datetime).getTime() / 1000)
          : null,
      };
    });
  
    onSubmit({ demos: updatedDemos });
    reset();
  };


  return (
    <form
    onSubmit={handleSubmit(handleFormSubmit)}
    className="bg-white rounded-lg shadow-sm border p-4 space-y-4"
  >
    {fields.map((field, index) => {
      const hasType = demosWatch[index]?.type;
      const selectedDatetime = demosWatch[index]?.datetime;

      // Compute preview text (for showing under name)
      let previewText = "Please select Type first";
      if (hasType && selectedDatetime) {
        const d = new Date(selectedDatetime);
        const dateStr = d.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
        const timeStr = d.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        previewText = `${hasType === "demo" ? "Demo" : "Training"} - ${dateStr} - ${timeStr}`;
      } else if (hasType && !selectedDatetime) {
        previewText = "Please select Date & Time";
      }

      return (
        <div
          key={field.id}
          className="grid grid-cols-1 md:grid-cols-12 w-fit gap-3 items-start border-b pb-4 last:border-none"
        >
          {/* Name */}
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Name
            </label>
            <p
              className={`text-sm mt-2 ${
                hasType && selectedDatetime
                  ? "text-gray-600 font-medium"
                  : "text-gray-500 italic"
              }`}
            >
              {previewText}
            </p>
            {errors.demos?.[index]?.demoName && (
              <p className="text-red-500 text-xs mt-1">
                {errors.demos[index].demoName.message}
              </p>
            )}
          </div>

          {/* Type */}
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              {...register(`demos.${index}.type`, {
                required: "Type is required",
              })}
              disabled={isLoadingSales}
              className={`w-full p-1.5 border rounded text-sm ${
                errors.demos?.[index]?.type
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            >
              <option value="">Select Type</option>
              <option value="demo">Demo</option>
              <option value="training">Training</option>
            </select>

            {errors.demos?.[index]?.type && (
              <p className="text-red-500 text-xs mt-1">
                {errors.demos[index].type.message}
              </p>
            )}
          </div>

          {/* Host By */}
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Host By
            </label>
            <select
              {...register(`demos.${index}.hostBy`, {
                required: "Host By is required",
              })}
              disabled={isLoadingSales}
              className="w-full p-1.5 border rounded text-sm border-gray-300"
            >
              <option value="">
                {isLoadingSales ? "Loading..." : "Select Host By"}
              </option>
              {salesEmployees?.employees.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.name} ({emp.employeeId})
                </option>
              ))}
            </select>
            {errors.demos?.[index]?.hostBy && (
              <p className="text-red-500 text-xs mt-1">
                {errors.demos[index].hostBy.message}
              </p>
            )}
          </div>

          {/* DateTime */}
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Date / Time
            </label>
            <input
              type="datetime-local"
              {...register(`demos.${index}.datetime`, {
                required: "Date and Time are required",
              })}
              className={`w-full p-1.5 border rounded text-sm ${
                errors.demos?.[index]?.datetime
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />
            {errors.demos?.[index]?.datetime && (
              <p className="text-red-500 text-xs mt-1">
                {errors.demos[index].datetime.message}
              </p>
            )}
          </div>

          {/* Action */}
          <div className="md:col-span-1 flex flex-col justify-end items-center">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Action
            </label>
            <button
              type="button"
              onClick={() => remove(index)}
              disabled={fields.length <= 1}
              className="text-red-500 hover:text-red-600 cursor-pointer"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      );
    })}

    {/* Buttons */}
    <div className="flex justify-end items-center mt-4 pt-3 border-t gap-3">
      <button
        type="button"
        onClick={() =>
          append({
            demoName: "",
            type: "",
            hostBy: "",
            datetime: "",
            createdBy: user?.employeeProfile?._id,
          })
        }
        className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
      >
        + Add New Function
      </button>
      <button
        type="submit"
        disabled={isLoading}
        className="px-5 py-2 text-sm font-medium text-white bg-[#2C4E86] rounded hover:bg-blue-900 disabled:bg-blue-300"
      >
        {isLoading ? "Submitting..." : `Submit ${fields.length} Function(s)`}
      </button>
    </div>
  </form>
  );
};
